import { UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  signal,
  untracked,
} from '@angular/core';
import {
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  type AbstractControl,
  type ControlValueAccessor,
  type ValidationErrors,
  type Validator,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

import { SUPPORTED_LANGUAGES, type SupportedLanguage, type TranslatableText } from '@zag/core/common';

import { ZagButton } from '../button/button';
import { FormField } from '../form-field/form-field';
import { Input } from '../input/input';
import { LanguageService } from '../services/language.service';
import { TranslatedPipe } from '../pipes/translated.pipe';

@Component({
  selector: 'zag-translatable-text-field',
  imports: [FormField, Input, TranslatePipe, TranslatedPipe, UpperCasePipe, ZagButton],
  templateUrl: './translatable-text-field.html',
  styleUrl: './translatable-text-field.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TranslatableTextField),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TranslatableTextField),
      multi: true,
    },
  ],
})
export class TranslatableTextField implements ControlValueAccessor, Validator {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly languageService = inject(LanguageService);

  readonly value = model<TranslatableText>({});
  readonly field = input<string>('value');
  readonly label = input<string>('');
  readonly hint = input<string>('');
  readonly error = input<string>('');
  readonly required = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly multiline = input<boolean>(false);
  readonly rows = input<number>(4);
  readonly id = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly placeholder = input<string>('');
  readonly inputClass = input<string>('');
  readonly ariaDescribedby = input<string | undefined>(undefined);

  readonly activeLanguage = signal<SupportedLanguage>(this.languageService.getCurrentLanguage());
  readonly languages = computed<readonly SupportedLanguage[]>(() => {
    const current = this.languageService.currentLanguage();
    return [current, ...SUPPORTED_LANGUAGES.filter(language => language !== current)];
  });
  readonly filledLanguages = computed<ReadonlySet<SupportedLanguage>>(() => {
    const filled = new Set<SupportedLanguage>();
    const translations = this.value();
    const field = this.field();

    for (const language of this.languages()) {
      const text = translations[language]?.[field];
      if (typeof text === 'string' && text.trim().length > 0) {
        filled.add(language);
      }
    }

    return filled;
  });
  readonly activeText = computed(() => {
    const text = this.value()[this.activeLanguage()]?.[this.field()];
    return typeof text === 'string' ? text : '';
  });
  readonly hasActiveText = computed(() => this.activeText().trim().length > 0);
  readonly isDisabled = computed(() => this.disabled() || this.disabledFromHost());
  readonly controlId = computed(() => this.id() ?? `translatable-text-field-${this.field()}`);
  readonly panelId = computed(() => `${this.controlId()}-panel`);
  readonly describedBy = computed(() => {
    const descriptionId = `${this.controlId()}-desc`;
    const externalDescriptionId = this.ariaDescribedby();
    return externalDescriptionId ? `${externalDescriptionId} ${descriptionId}` : descriptionId;
  });
  readonly inputRequired = computed(() => this.required() && this.activeLanguage() === this.languages()[0]);

  private readonly userHasSelectedLanguage = signal(false);
  private readonly disabledFromHost = signal(false);
  private onChange: (value: TranslatableText) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private validatorChange: () => void = () => undefined;

  constructor() {
    effect(() => {
      const currentLanguage = this.languageService.currentLanguage();
      this.filledLanguages();
      this.required();

      if (!untracked(this.userHasSelectedLanguage) && untracked(this.activeLanguage) !== currentLanguage) {
        this.activeLanguage.set(currentLanguage);
      }

      this.validatorChange();
      this.cdr.markForCheck();
    });
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (!this.required() || this.filledLanguages().size > 0) {
      return null;
    }

    return { requiredPrimaryLanguage: { language: this.languages()[0] } };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChange = fn;
  }

  writeValue(value: TranslatableText | null | undefined): void {
    this.value.set(value ?? {});
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: TranslatableText) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledFromHost.set(isDisabled);
    this.cdr.markForCheck();
  }

  protected selectLanguage(language: SupportedLanguage): void {
    if (this.isDisabled()) {
      return;
    }

    this.userHasSelectedLanguage.set(true);
    this.activeLanguage.set(language);
  }

  protected onTabKeydown(event: KeyboardEvent, index: number): void {
    const nextIndex = this.navigationIndex(event.key, index);
    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    const nextLanguage = this.languages()[nextIndex];
    if (!nextLanguage) {
      return;
    }

    this.selectLanguage(nextLanguage);
    this.focusTab(nextIndex);
  }

  protected onTextChange(text: string): void {
    if (this.isDisabled() || this.readonly()) {
      return;
    }

    const translations = this.value();
    const language = this.activeLanguage();
    const field = this.field();
    const languageSlot = { ...(translations[language] ?? {}), [field]: text };
    const nextValue: TranslatableText = { ...translations, [language]: languageSlot };

    this.value.set(nextValue);
    this.onChange(nextValue);
  }

  protected onBlur(): void {
    this.onTouched();
  }

  protected tabId(language: SupportedLanguage): string {
    return `${this.controlId()}-tab-${language}`;
  }

  private navigationIndex(key: string, index: number): number | null {
    const languages = this.languages();
    if (key === 'Home') {
      return 0;
    }

    if (key === 'End') {
      return languages.length - 1;
    }

    if (key !== 'ArrowRight' && key !== 'ArrowLeft') {
      return null;
    }

    const offset = key === 'ArrowRight' ? 1 : -1;
    return (index + offset + languages.length) % languages.length;
  }

  private focusTab(index: number): void {
    if (typeof document === 'undefined') {
      return;
    }

    const language = this.languages()[index];
    if (!language) {
      return;
    }

    const tab = document.getElementById(this.tabId(language));
    if (tab instanceof HTMLElement) {
      tab.focus();
    }
  }
}
